import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Form, FormValues, FormApi } from 'informed';
import { Button } from 'reactstrap';
import { assign as _assign } from 'lodash';
import {
  ConfigurationModel, 
  UserModel,
  UserDataModel,
} from 'franke-trough-dependencies';

import {
  NewConfigurationFormModel,
  newConfigurationFormValues,
  InputValue,
  renderFields,
  GlobalPrivateState,
  formFieldsToFormValues,
  FormDefaultState,
  formDefaultState,
  InputModel,
} from '../../../../api';

import { requestAPIActions, RequestApiResult, RequestApiHttpPayload, RequestApiError} from 'loopback-react-request';
import { Redirect } from 'react-router';

import './ConfigurationNewForm.scss';
import { RouterState } from 'connected-react-router';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './ConfigurationNewForm.messages';
import { formatPeriod } from '../../Admin/Pricing/Pricings';
import { userIsInternal } from '../../Admin/utils';

interface ConfigurationNewFormProps {
  configuration: RequestApiResult<ConfigurationModel>;
  dispatch: Dispatch;
  formFields?: NewConfigurationFormModel;
  location: RouterState['location'];
  user: RequestApiResult<UserModel>;
  userData: RequestApiResult<UserDataModel>;
  pricings: GlobalPrivateState['pricings'];
  queryState: GlobalPrivateState['queryState'];
}

interface ConfigurationNewFormState extends FormDefaultState<NewConfigurationFormModel> {
  loading: boolean;
  patch: boolean;
  requestError?: RequestApiError;
  requestSuccess: boolean;
  submitValues: {[key: string]: InputValue};
  submitted: boolean;
  pricingOptionsSet: boolean;
}

class ConfigurationNewFormComponent extends React.Component<ConfigurationNewFormProps, ConfigurationNewFormState> {
  constructor(props: ConfigurationNewFormProps) {
    super(props);
    this.getFormApi = this.getFormApi.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handlePreSubmit = this.handlePreSubmit.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    
    if (!props.formFields) {
      Object.keys(newConfigurationFormValues).forEach(key => {
        const input = newConfigurationFormValues[key] as InputModel;
        if (input && input.value && (input.value as string).length > 0) {
          input.value = '';
        }
        newConfigurationFormValues[key] = input;
      });
    } else {
      // Delete periods for rename...
      delete props.formFields['pricingsAccessories'];
      delete props.formFields['pricingsWashbasin'];
    }
    
    this.state = {
      formFields: props.formFields || newConfigurationFormValues(),
      formHasErrors: true,
      formState: formDefaultState,
      loading: false,
      patch: props.formFields ? true : false,
      requestSuccess: false,
      submitValues: {},
      submitted: false,
      pricingOptionsSet: false
    };
  }

  componentDidUpdate(prevProps: ConfigurationNewFormProps) {    
    const {configuration, pricings} = this.props;
    const prevConfiguration = prevProps.configuration;
    const prevPricings = prevProps.pricings;
    const {loading, formFields, formApi, patch} = this.state;
    
    const isFormForRename = patch ? true : false;
    if (!isFormForRename) {
      if (configuration.updating && !loading) {
        this.setState({loading: true});
      }
      
      if (configuration.result && loading) {
        this.setState({loading: false});
  
        if (configuration.result && !configuration.updating && configuration.loaded) {
          this.setState({requestSuccess: true});
        }
      }
  
      if (configuration.error && loading) {      
        if (formApi && formFields) {
          const values = {};
          Object.keys(formFields).map(key => _assign(values, {[key]: formFields[key].value}));
          formApi.setValues(values);
        }
  
        this.setState({
          requestError: configuration.error,
          loading: false,
        });
      }   
    } else {
      if (configuration.updating && !loading) {
        this.setState({loading: true});
      }
      if (configuration.result && loading) {
        
        if (configuration.result && !configuration.updating && configuration.loaded) {
          this.setState({loading: false});
          if ((prevConfiguration.result && prevConfiguration.result.updated_at) !== configuration.result.updated_at) {
            this.setState({requestSuccess: true});
          }
        }
      }
    }

    // Populate pricing periods
    if (!this.state.pricingOptionsSet && pricings.result) {
      const formFields = this.state.formFields;

      if (formFields) {
        const pricingsOptions = pricings.result.map((pricing: any) => {
          if (pricing.deleted) { return }
          return {
            type: pricing.type,
            label: formatPeriod(pricing.dateFrom, pricing.dateUntil),
            value: pricing.id,
            disabled: false
          }
        });

        if (formFields['pricingsAccessories']) {
          const pricingsOptionsAccessories = pricingsOptions.filter((pricing: any) => pricing && pricing.type === 'accessories');
          formFields['pricingsAccessories'].options = [
            ...formFields['pricingsAccessories'].options,
            ...pricingsOptionsAccessories as any
          ];
        }

        if (formFields['pricingsWashbasin']) {
          const pricingsOptionsWashbasin = pricingsOptions.filter((pricing: any) => pricing && pricing.type === 'washbasin');
          formFields['pricingsWashbasin'].options = [
            ...formFields['pricingsWashbasin'].options,
            ...pricingsOptionsWashbasin as any
          ];
        }

        this.setState({
          formFields,
          pricingOptionsSet: true
        });
      }
    }
  }

  render() {
    const { configuration, pricings } = this.props;
    const { formFields, formHasErrors, requestError, requestSuccess } = this.state;
    const initialValues = formFieldsToFormValues(formFields);

    return (
      <div className="partial-project-new-form my-0 mx-auto">
        <Form
          getApi={this.getFormApi}
          initialValues={initialValues}
          onSubmit={this.handleSubmit}
          onValueChange={this.handleChange}
        >
          <div className="row m-0">
            <div className="col-12">
              {requestError && (
                <p className="text-danger">
                  {requestError.message}
                </p>
              )}
            </div>
          </div>
          <div className="row m-0">
            {formFields && Object.keys(formFields).map((fieldKey: string, index: number) => {
              const key = `form_field_${fieldKey}_${index}`;
              const field = formFields[fieldKey] as any;

              const hasNoOptions = field.options && field.options.length <= 1;
              const isExternalUser = field.type === 'select' && this.props.user && this.props.user.result && !userIsInternal(this.props.user.result);
              if (hasNoOptions || isExternalUser) {
                return;
              }

              return (
                <div key={key} className={field.col ? `col-12 col-sm-${field.col}` : 'col-12'}>
                  {renderFields<NewConfigurationFormModel>({
                    field: field,
                    formState: this.state.formState,
                    index: fieldKey,
                  })}
                </div>
              );
            })}
          </div>
          <div className="row m-0">
            <div className="col-12 text-center">
              <Button
                className="register-button mt-3" 
                type="submit"
                disabled={formHasErrors || pricings.updating}
              >
                <FormattedMessage {...MESSAGES.configurationNewFormButton} />
              </Button>
            </div>
          </div>
        </Form>
        {requestSuccess && configuration && configuration.result && configuration.result.id && (
          <Redirect to={this.state.patch ? '/' : `/configuration/${configuration.result.id}/project`} />
        )}
      </div>
    );
  }

  private getFormApi(formApi: FormApi<FormValues>) {
    this.setState({formApi});
  }

  private handleChange(value: FormValues) {
    if (!this.state.formApi) {
      return;
    }
    
    const isFormForRename = this.state.patch ? true : false;
    const isInternalUser = this.props.user && this.props.user.result && userIsInternal(this.props.user.result);
    const formState = this.state.formApi.getState();
    const {errors, values} = formState;
    const errorKeys = errors && Object.keys(errors);
    const formHasErrors = (errorKeys && errorKeys.length > 0) || !isFormForRename && isInternalUser && Object.keys(values).length < 3 ? true : false;
   
    this.setState({
      formState,
      formHasErrors,
      formErrors: !formState.pristine ? formState.errors : {},
    });
  }

  private handlePreSubmit(values: FormValues): FormValues {
    const {
      user,
      userData,
    } = this.props;

    const { 
      formState, 
      formFields,
      // submitValues, 
    } = this.state;

    // _assign(submitValues, values);

    if (user && user.result) {
      values.userId = user.result.id;
    }

    if (userData && userData.result) {
      values.userdataId = userData.result.id;
    }

    const folderId = this.props.queryState.params.folderId;
    if (folderId) {
      values.folderId = folderId;
    }


    values.height = 0;
    values.width = 0;

    if (formFields) {
      Object.keys(formFields).map(key => formFields[key].value = values[key]);
    }

    this.setState({
      formState,
      // submitValues,
    });

    return values;
  }

  private handleSubmit(values: FormValues): void {
    const {formHasErrors} = this.state;
    if (!formHasErrors) {
      this.dispatchRegister(values);
    }
  }

  private dispatchRegister(values: FormValues) {
    const { userData } = this.props;
    const { patch } = this.state; 

    if (userData && userData.result) {
      const valuesToDispatch = this.handlePreSubmit(values);
      const configurationId = this.props.configuration.result && this.props.configuration.result.id;
      const url = configurationId && patch ? `/Configurations/${configurationId}` : `/UserData/${userData.result.id}/Configurations`;
      const method = patch ? 'PATCH' : 'POST';

      this.props.dispatch(requestAPIActions.load<RequestApiHttpPayload>(
        'configuration', {
          url,
          options: {
            method,
            data: valuesToDispatch,
          },
        },
        true
      ));
  
      this.setState({
        submitted: true,
        formHasErrors: false,
      });
      
    }
  }

}

export const ConfigurationNewForm = connect((state: GlobalPrivateState) => ({
  configuration: state.configuration,
  location: state.router.location,
  userData: state.userData,
  user: state.user,
  queryState: state.queryState,
  pricings: state.pricings
}))(ConfigurationNewFormComponent);

